import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Inbox } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import Spinner from './Spinner';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import './MqQueueModal.css';

// WHAT IS WAITING FOR A TARGET THAT COLLECTS ITS OWN MESSAGES.
//
// A target marked `polls /mq` is never called, so "did it arrive?" cannot be answered by looking at a
// response — there is no response. The queue is the answer, and until now the only way to see it was to
// go to the vendor list and read a number.
//
// TWO NUMBERS DO THE WORK, and they answer different halves of the same support call:
//
//   WAITING          "we are not receiving anything" starts here.
//   LAST COLLECTED   what separates a vendor with nothing to collect from one that has STOPPED
//                    polling. On an empty queue those look identical and mean opposite things.
//
// NO PAYLOADS. This is a list of what is waiting; one message's body is a different question and
// belongs to the message log. Fetching a MEDIUMTEXT per row would make opening this cost as much as
// opening every message in it.

const MqQueueModal = ({ isOpen, onClose, integrationId, accessId, systemName, propertyName }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!integrationId || !accessId) return;
        setLoading(true);
        setError(null);
        try {
            setData(await integrationsService.getQueue(integrationId, accessId));
        } catch (e) {
            setError(apiErrorMessage(e, 'The queue could not be read'));
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [integrationId, accessId]);

    useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md acm-modal mqm" onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="mqm-title">
                <div className="modal-header">
                    <h2 id="mqm-title">
                        Queue for {systemName || 'this target'}
                        {propertyName && <span className="acm-hint"> · {propertyName}</span>}
                    </h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <p className="acm-hint mqm-lede">
                        {systemName || 'This target'} is never called — it polls <code>GET /mq</code> with
                        its own credential and takes what is waiting. These are the messages it has not
                        collected yet.
                    </p>

                    {error && <div className="os-msg os-msg-error">{error}</div>}

                    {loading && !data ? <div className="loading-section"><Spinner /></div> : data && (
                        <>
                            <div className="mqm-tally">
                                <span className="mqm-stat">
                                    <strong>{data.depth}</strong> waiting
                                </span>
                                <span className="mqm-stat mqm-muted">
                                    last collected{' '}
                                    <strong>
                                        {data.lastCollectedAt ? formatDateTime(data.lastCollectedAt) : 'never'}
                                    </strong>
                                </span>
                                <span className="mqm-grow" />
                                <button type="button" className="btn btn-secondary btn-sm"
                                    onClick={load} disabled={loading}>
                                    <RefreshCw size={14} /> Refresh
                                </button>
                            </div>

                            {/* NEVER COLLECTED AND NOTHING WAITING IS NOT "FINE". It is a vendor that has
                                never polled, which on a queue of zero looks exactly like one that is
                                perfectly up to date. */}
                            {!data.depth && !data.lastCollectedAt && (
                                <div className="os-msg os-msg-warn mqm-note">
                                    Nothing is waiting and this vendor has never collected anything. If it
                                    should be polling, it has not started.
                                </div>
                            )}

                            {data.messages.length === 0 ? (
                                <p className="mqm-empty"><Inbox size={16} /> The queue is empty.</p>
                            ) : (
                                <table className="acp-table mqm-table">
                                    <thead>
                                        <tr>
                                            <th>Queued</th><th>Message</th><th>x-hotelid</th>
                                            <th>From</th><th>Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.messages.map(m => (
                                            <tr key={m.id}>
                                                <td className="mqm-muted">{formatDateTime(m.created_at)}</td>
                                                <td>
                                                    <div>{m.message_type}</div>
                                                    <div className="mqm-txn" title={m.transaction_id}>
                                                        {m.transaction_id}
                                                    </div>
                                                </td>
                                                <td className="mqm-muted">{m.uapi_hotel_id || '—'}</td>
                                                <td className="mqm-muted">
                                                    {m.source_method ? `${m.source_method} ` : ''}
                                                    {m.source_operation || '—'}
                                                </td>
                                                <td className="mqm-muted">
                                                    {Math.max(1, Math.round((m.payload_bytes || 0) / 1024))} KB
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {data.truncated && (
                                <p className="acm-hint mqm-note">
                                    Showing the oldest {data.messages.length} of {data.depth}. They are
                                    collected in this order.
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default MqQueueModal;
